import Section from '../../../components/Main/Section/Section';
import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { v4 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';
import ActionBar from '../../../components/ActionBar/ActionBar';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import { AuthContext } from '../../../providers/AuthProvider';
import { ISurveysEntry } from '../../../shared/interfaces/ISurveysEntry';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import SurveyService from '../../../services/SurveyService';
import { useNavigate, useParams } from 'react-router-dom';
import { Add, Check, OpenInNew } from '@mui/icons-material';
import {
  ICustomSurveyQuestion,
  QuestionLocalisation,
} from '../../../shared/interfaces/ICustomSurveyQuestion';
import SurveyQuestion from './SurveyQuestion';
import { SortableList } from '../../../components/Dnd/SortableList';
import SurveyUtils from '../../../shared/SurveyUtils';
import i18n from 'i18next';
import { isQuestionType, QuestionTypes } from '../../../shared/interfaces/ISurvey';
import {
  createDefaultMatrixColumns,
  createDefaultMatrixRows,
  MATRIX_DEFAULT_QUESTION,
} from '../../../shared/helpers/SurveyMatrix';

const SurveyQuestions = () => {
  const { hasRole } = useContext(AuthContext);
  const { setCurrentView } = useContext(CurrentViewContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const params = useParams();
  const [survey, setSurvey] = useState<ISurveysEntry | null>(null);
  const [questions, setQuestions] = useState<ICustomSurveyQuestion[]>([]);
  const [newType, setNewType] = useState<QuestionTypes>('CUSTOMSINGLE');
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    setCurrentView(t('survey_tool'));
    document.title = `${t('app_branding')} ${t('survey_tool')} `;
  }, [t]);

  const reload = async () => {
    const controller = new AbortController();
    await getSurveys(controller.signal);
  };

  const reorderAndReload = async (deletedQid: string | undefined) => {
    reorderQuestions(questions, deletedQid);
    await reload();
  };

  const getSurveys = async (signal: AbortSignal) => {
    const response = await SurveyService.getSurvey(setLoading, signal);
    if (response) {
      for (let i = 0; i < response.length; i++) {
        if (response[i].id == params.id) {
          setSurvey(response[i]);
          await getQuestions(response[i], signal);
        }
      }
    }
  };

  const getQuestions = async (ls: ISurveysEntry, signal: AbortSignal) => {
    const response = await SurveyService.getQuestions(ls.id, ls.type, setLoading, signal);
    if (response) {
      // set the id so we can use the base item for sorting
      response.forEach((q: ICustomSurveyQuestion) => (q.id = q.questionId!));
      setQuestions(response);
    }
  };

  const createExampleQuestion = (type: QuestionTypes) => {
    let newPosition = 1;
    if (!type) {
      type = 'CUSTOMSINGLE';
    }
    if (questions) {
      newPosition = questions.length + 1;
    }
    // this id is only for sorting
    const id = uuid();
    switch (type) {
      case 'CUSTOMSCALENPS':
        return {
          id: id,
          type: type,
          question: 'Wie wahrscheinlich ist es, dass Sie uns weiterempfehlen?',
          questionI18n: {} as QuestionLocalisation,
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [],
        };
      case 'CUSTOMTEXT':
        return {
          id: id,
          type: type,
          question: 'Bitte nennen Sie uns Ihre Meinung zu ...',
          questionI18n: {} as QuestionLocalisation,
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [],
          ShouldShowPrivacyNotice: false,
          PrivacyNoticeText:
            'Ich stimme zu, dass meine Daten zu Analysezwecken gespeichert werden.',
          PrivacyNoticeLink: '',
        };
      case 'CUSTOMYESNO':
        return {
          id: id,
          type: type,
          question: 'Würden Sie uns weiterempfehlen?',
          questionI18n: {} as QuestionLocalisation,
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [{ label: i18n.t('yes') }, { label: i18n.t('no') }],
        };
      case 'CUSTOMMULTIPLE':
        return {
          id: id,
          type: type,
          question: 'Welche Option(en) bevorzugen Sie?',
          questionI18n: {} as QuestionLocalisation,
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [{ label: 'eins' }, { label: 'zwei' }, { label: 'drei' }],
        };
      case 'CUSTOMGATE':
        return {
          id: id,
          type: type,
          question: 'Würden Sie uns weiterempfehlen?',
          questionI18n: {} as QuestionLocalisation,
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [{ label: 'yes' }, { label: 'no' }],
        };
      case 'CUSTOMFEEDBACK':
        return {
          id: id,
          type: type,
          question: 'Was können wir verbessern?',
          questionI18n: {} as QuestionLocalisation,
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [],
          ShouldShowPrivacyNotice: false,
          PrivacyNoticeText:
            'Ich stimme zu, dass meine Daten zu Analysezwecken gespeichert werden.',
          PrivacyNoticeLink: '',
        };
      case 'CUSTOMMATRIX':
        return {
          id: id,
          type: type,
          question: MATRIX_DEFAULT_QUESTION,
          questionI18n: {} as QuestionLocalisation,
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: createDefaultMatrixColumns(),
          rows: createDefaultMatrixRows(),
        };
      default:
        return {
          id: id,
          type: type,
          question: 'Welche Option bevorzugen Sie?',
          questionI18n: {} as QuestionLocalisation,
          surveyId: '' + params.id,
          optional: false,
          position: newPosition,
          score: 0,
          options: [{ label: 'eins' }, { label: 'zwei' }, { label: 'drei' }],
        };
    }
  };

  const hasGateQuestion = () => questions.some((q) => q.type === 'CUSTOMGATE');
  const hasFeedbackQuestion = () => questions.some((q) => q.type === 'CUSTOMFEEDBACK');

  const handleAdd = async () => {
    if (newType === 'CUSTOMGATE' && hasGateQuestion()) return;
    setLoading(true);
    const dataset = createExampleQuestion(newType);

    await SurveyService.addQuestion(dataset.surveyId, survey?.type || '', dataset);
    await reload();
  };

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      await getSurveys(controller.signal);
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const onDragEnd = (questionsReordered: ICustomSurveyQuestion[]) => {
    reorderQuestions(questionsReordered);
  };

  const reorderQuestions = async (
    questionsReordered: ICustomSurveyQuestion[],
    deletedQid?: string
  ) => {
    let isAfterSelectionQuestion = false;
    for (let question of questionsReordered) {
      if (question.type === 'CUSTOMGATE') {
        if (question.questionId !== deletedQid) {
          isAfterSelectionQuestion = true;
        }
      } else if (question.type === 'CUSTOMFEEDBACK' && !isAfterSelectionQuestion) {
        setErrorMessage(t('feedback_question_sort_error'));
        return false;
      }
    }

    let position = 1;
    const keep: any[] = [];
    for (let i = 0; i < questionsReordered.length; i++) {
      const currentQuestionId = questionsReordered[i].questionId || '';
      if (deletedQid && deletedQid === currentQuestionId) {
        if (survey) {
          await SurveyService.deleteQuestion(survey.type, survey.id, deletedQid);
        }
        continue;
      }
      questionsReordered[i].position = position;
      await SurveyService.updateQuestion(
        survey?.type!,
        survey?.id!,
        currentQuestionId,
        questionsReordered[i]
      );
      position++;
      keep.push(questionsReordered[i]);
    }
    setQuestions(keep);
  };

  const renderQuestions = () => {
    if (!survey) {
      return null;
    }
    if (!questions) {
      return null;
    }

    return (
      <SortableList
        items={questions}
        onChange={onDragEnd}
        renderItem={(item) => (
          <SortableList.Item id={item.id}>
            <SurveyQuestion
              survey={survey}
              question={item}
              reorderAndReload={reorderAndReload}
              reload={reload}
            />
            <SortableList.DragHandle />
          </SortableList.Item>
        )}
      />
    );
  };

  return survey && hasRole('can-use-survey') ? (
    <Section>
      <ContentContainer>
        <LeftContainer>
          <Snackbar
            open={errorMessage !== ''}
            autoHideDuration={10000}
            onClose={() => setErrorMessage('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              severity={'error'}
              onClose={() => setErrorMessage('')}
            >
              {errorMessage}
            </Alert>
          </Snackbar>

          <ActionBar
            sx={{ mb: theme.spacing(2) }}
            children={<span>{survey.label}</span>}
          />

          {renderQuestions()}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(2),
              my: theme.spacing(2),
            }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: '17.75rem' }}
            >
              <InputLabel id="select-label">{t('select_survey_question_type')}</InputLabel>
              <Select
                labelId="select-label"
                label={t('select_survey_question_type')}
                value={newType || ''}
                onChange={(e: SelectChangeEvent) => {
                  if (isQuestionType(e.target.value)) {
                    setNewType(e.target.value);
                  }
                }}
              >
                <MenuItem value={'CUSTOMSINGLE'}>{t('survey_question_customsingle')}</MenuItem>
                <MenuItem value={'CUSTOMMULTIPLE'}>{t('survey_question_custommultiple')}</MenuItem>
                <MenuItem value={'CUSTOMMATRIX'}>{t('survey_question_custommatrix')}</MenuItem>
                <MenuItem value={'CUSTOMDROPDOWN'}>{t('survey_question_customdropdown')}</MenuItem>
                <MenuItem value={'CUSTOMYESNO'}>{t('survey_question_customyesno')}</MenuItem>
                <MenuItem value={'CUSTOMSCALENPS'}>{t('survey_question_customscalenps')}</MenuItem>
                <MenuItem value={'CUSTOMTEXT'}>{t('survey_question_customtext')}</MenuItem>
                {survey?.platform && survey?.platformUrl && (
                  <MenuItem
                    disabled={hasGateQuestion()}
                    value={'CUSTOMGATE'}
                  >
                    <Tooltip title={t('selection_question_info')}>
                      <Typography>{t('survey_question_customgate')}</Typography>
                    </Tooltip>
                  </MenuItem>
                )}
                {survey?.platform && survey?.platformUrl && (
                  <MenuItem
                    disabled={!hasGateQuestion()}
                    value={'CUSTOMFEEDBACK'}
                  >
                    <Tooltip title={t('feedback_question_info')}>
                      <Typography>{t('survey_question_feedback')}</Typography>
                    </Tooltip>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAdd}
            >
              {t('add_question')}
            </Button>
            <Button
              href={`${SurveyUtils.getPublicUrl(survey.type, survey.slug)}?preview=true`}
              target="_blank"
              variant="contained"
              startIcon={<OpenInNew />}
            >
              {t('preview')}
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/research/surveytool/guide/')}
              startIcon={<Check />}
            >
              {t('complete')}
            </Button>
          </Box>
        </LeftContainer>
      </ContentContainer>
    </Section>
  ) : null;
};

export default SurveyQuestions;
